import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Activity, ChevronDown, ChevronUp, Crown, DollarSign, Eye, Inbox as InboxIcon, X } from 'lucide-react';
import { useUserAuth } from '../contexts/UserAuthContext';
import { FounderPushIcon } from './FounderPushToggle';
import { createApiClient } from '../lib/apiClient';

const api = createApiClient();

const formatRelative = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

const TYPE_DOT = {
  brand: 'bg-yellow-300',
  school: 'bg-purple-400',
  athlete: 'bg-blue-400',
  contact: 'bg-rose-400',
  pageview: 'bg-emerald-400',
  upgrade: 'bg-yellow-400 ring-2 ring-yellow-200',
};

/**
 * FounderLiveStats — floating overlay widget visible ONLY to founders
 * (server-gated via /api/founder/live-stats which 403s for everyone else)
 * AND only when the user has chosen the "Founders View" persona.
 *
 * Polls every 10 seconds. Stays out of the way on /admin and /login.
 */
const FounderLiveStats = () => {
  const { user } = useUserAuth();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [premium, setPremium] = useState(null);
  // iter454 · Default MINIMIZED on mobile (<640px viewport) or on any
  // athlete profile route so the 270-300px expanded card never covers
  // the Quick Stats tap targets on the redesigned network profile.
  // Founder can still expand with the chevron. Desktop keeps its
  // expanded default because there's plenty of room off to the side.
  const _isAthleteProfileRoute =
    typeof window !== 'undefined' &&
    /^\/(?:visionary\/)?(?:network\/)?[^/]+\/?$/.test(location.pathname) &&
    location.pathname !== '/';
  const _isMobile =
    typeof window !== 'undefined' && window.innerWidth < 640;
  const [minimized, setMinimized] = useState(_isMobile || _isAthleteProfileRoute);
  const [hidden, setHidden] = useState(false);
  const tickRef = useRef(null);
  const premiumTickRef = useRef(null);

  const enabled = !!user?.is_founder && user?.persona === 'founder';
  const routeBlocked = location.pathname.startsWith('/admin') ||
                       location.pathname.startsWith('/Penelopesims') ||
                       location.pathname === '/login' ||
                       location.pathname.startsWith('/inbox');

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/founder/live-stats');
      setData(res.data);
    } catch (_) { /* silent */ }
  }, []);

  const fetchPremium = useCallback(async () => {
    try {
      const res = await api.get('/founder/premium-metrics');
      setPremium(res.data);
    } catch (_) { /* silent */ }
  }, []);

  useEffect(() => {
    if (!enabled || routeBlocked || hidden) return undefined;
    fetchStats();
    fetchPremium();
    tickRef.current = setInterval(fetchStats, 10000);
    premiumTickRef.current = setInterval(fetchPremium, 30000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      if (premiumTickRef.current) clearInterval(premiumTickRef.current);
    };
  }, [enabled, routeBlocked, hidden, fetchStats, fetchPremium]);

  const activeNow = data?.active_now ?? 0;
  const subs = data?.submissions_last_24h || {};
  const feed = useMemo(() => (data?.recent_activity || []).slice(0, 8), [data]);

  if (!enabled || routeBlocked || hidden) return null;

  // Lift the widget further above the BottomNav when collapsed so the
  // 5-tab BottomNav buttons stay tappable underneath. Expanded sits a
  // touch lower since the longer card already clears the nav.
  // Note: the BottomNav floats ~5rem off the bottom edge on mobile, so
  // we add a comfortable 1.5rem buffer.
  return (
    <div
      className="fixed z-[65] right-3 sm:right-5 pointer-events-none transition-[bottom] duration-300"
      style={{ bottom: minimized ? 'calc(env(safe-area-inset-bottom, 0px) + 5.25rem)' : 'calc(env(safe-area-inset-bottom, 0px) + 5rem)' }}
      data-testid="founder-live-stats"
    >
      {/* Liquid Glass — heavy backdrop blur + saturation + soft inner highlight
          so the widget reads as a frosted lozenge floating over the page,
          matching the BottomNav aesthetic. When minimized it shrinks to fit
          the LIVE strip so there's less to dodge with the thumb. */}
      <div className={`pointer-events-auto rounded-[26px] overflow-hidden text-white relative transition-[width] duration-200 ${
        minimized ? 'w-auto' : 'w-[270px] sm:w-[300px]'
      }`}>
        {/* Frosted background layer */}
        <div
          className="absolute inset-0 rounded-[26px] bg-white/8"
          style={{
            backdropFilter: 'blur(28px) saturate(180%)',
            WebkitBackdropFilter: 'blur(28px) saturate(180%)',
            backgroundColor: 'rgba(20, 20, 22, 0.55)',
          }}
        />
        {/* Subtle inner top highlight + outer ring for the glass edge */}
        <div className="absolute inset-0 rounded-[26px] pointer-events-none ring-1 ring-white/15" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none" />
        {/* Drop shadow */}
        <div className="absolute inset-0 rounded-[26px] shadow-[0_20px_50px_-10px_rgba(0,0,0,0.65)] pointer-events-none" />

        {/* Content sits above the glass layers */}
        <div className="relative">
        {/* Header — content right-aligned and grouped tightly so LIVE +
            active count sit next to the action icons (no awkward gap in
            the middle). Taller padding (py-3.5) makes the icons easier to
            hit with a thumb. When minimized this row IS the whole widget. */}
        <div className={`flex items-center justify-end gap-2 px-3.5 py-3.5 ${minimized ? '' : 'border-b border-white/10'}`}>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[11px] uppercase tracking-widest font-bold text-emerald-300">Live</span>
          </span>
          <span className="text-xs font-bold whitespace-nowrap">{activeNow} active</span>
          <div className="flex items-center gap-0.5 pl-1">
            <FounderPushIcon userId={user?.user_id} />
            <button
              type="button"
              onClick={() => setMinimized(m => !m)}
              className="p-1.5 rounded-full hover:bg-white/10 active:scale-95"
              aria-label={minimized ? 'Expand' : 'Minimize'}
              data-testid="founder-live-toggle"
            >
              {minimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <button
              type="button"
              onClick={() => setHidden(true)}
              className="p-1.5 rounded-full hover:bg-white/10 active:scale-95"
              aria-label="Hide widget"
              data-testid="founder-live-hide"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {!minimized && (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-3 gap-1.5 px-3 pt-3">
              <Kpi label="Views/hr" value={data?.views_last_hour ?? '—'} icon={<Eye className="w-3 h-3" />} />
              <Kpi label="Today" value={data?.views_today ?? '—'} icon={<Activity className="w-3 h-3" />} />
              <Kpi label="24h forms" value={subs.total ?? 0} icon={<InboxIcon className="w-3 h-3" />} />
            </div>

            {/* Premium & MRR — money row. Gold ring + emerald accent so
                the founder's revenue moment sits above the funnel data. */}
            <div className="grid grid-cols-2 gap-1.5 px-3 pt-1.5" data-testid="founder-live-premium-row">
              <div className="rounded-xl bg-yellow-500/10 ring-1 ring-yellow-400/40 px-2 py-1.5 backdrop-blur-md">
                <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-yellow-200">
                  <Crown className="w-3 h-3" />
                  <span>Premium</span>
                </div>
                <div className="text-sm font-bold mt-0.5 text-yellow-100" data-testid="founder-live-premium-count">
                  {premium?.total_premium ?? '—'}
                  {premium && (
                    <span className="ml-1 text-[9px] font-normal text-yellow-200/80">
                      {premium.total_paid || 0}p · {premium.total_trialing || 0}t
                    </span>
                  )}
                </div>
              </div>
              <div className="rounded-xl bg-emerald-500/10 ring-1 ring-emerald-400/40 px-2 py-1.5 backdrop-blur-md">
                <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-emerald-200">
                  <DollarSign className="w-3 h-3" />
                  <span>MRR</span>
                </div>
                <div className="text-sm font-bold mt-0.5 text-emerald-100" data-testid="founder-live-mrr">
                  {premium ? `$${Math.round(premium.mrr || 0).toLocaleString()}` : '—'}
                </div>
              </div>
            </div>

            {/* Submissions breakdown */}
            <div className="px-3 mt-2 flex items-center gap-1 text-[10px] flex-wrap">
              {[
                ['brand', 'Brand', subs.brand || 0],
                ['school', 'School', subs.school || 0],
                ['athlete', 'Athlete', subs.athlete || 0],
                ['contact', 'Contact', subs.contact || 0],
              ].map(([k, lbl, n]) => (
                <span key={k} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/5 ring-1 ring-white/10">
                  <span className={`w-1.5 h-1.5 rounded-full ${TYPE_DOT[k]}`} />
                  <span className="text-gray-300">{lbl}</span>
                  <span className="font-bold">{n}</span>
                </span>
              ))}
            </div>

            {/* Feed */}
            <div className="px-3 pt-3 pb-2">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Recent activity</div>
              <ul className="space-y-1 max-h-[180px] overflow-y-auto no-scrollbar">
                {feed.length === 0 && (
                  <li className="text-[11px] text-gray-500">Quiet right now…</li>
                )}
                {feed.map((row, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-[11px]">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${TYPE_DOT[row.type] || 'bg-gray-400'}`} />
                    <span className="truncate text-gray-200">
                      {row.kind === 'submission' ? (
                        <><span className="font-bold capitalize">{row.type}</span> · {row.label}</>
                      ) : row.kind === 'upgrade' ? (
                        <><span className="font-bold text-yellow-300">Premium</span> · {row.label}</>
                      ) : (
                        <><span className="text-gray-500">view</span> {row.label}</>
                      )}
                    </span>
                    <span className="ml-auto text-[10px] text-gray-500 shrink-0">{formatRelative(row.timestamp)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Link
              to="/inbox"
              className="block text-center text-[11px] font-bold uppercase tracking-widest py-2 border-t border-white/10 hover:bg-white/5"
              data-testid="founder-live-open-inbox"
            >
              Open Founder Inbox →
            </Link>
          </>
        )}
        </div>
      </div>
    </div>
  );
};

const Kpi = ({ label, value, icon }) => (
  <div className="rounded-xl bg-white/[0.07] ring-1 ring-white/15 px-2 py-1.5 backdrop-blur-md">
    <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-gray-300">
      {icon}
      <span>{label}</span>
    </div>
    <div className="text-sm font-bold mt-0.5">{value}</div>
  </div>
);

export default FounderLiveStats;
